import { onMounted, onUnmounted } from 'vue'
import type { Router } from 'vue-router'

/**
 * Registers CM-persona keyboard shortcuts:
 *   Cmd/Ctrl+K → focus header search input
 *   N → navigate to pipeline with add-deal form open
 *   L → navigate to today with log-activity form open
 *   P → navigate to pipeline
 *
 * Only fires when the user is NOT typing in an input, textarea, or contenteditable element.
 * The Cmd/Ctrl+K shortcut fires regardless of focus context (and prevents default browser search).
 */
export function useCmShortcuts(
  router: Router,
  focusSearch: () => void,
  isActive: () => boolean
) {
  function isTyping(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null
    if (!target) return false
    const tag = target.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA') return true
    if (target.isContentEditable) return true
    return false
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (!isActive()) return

    // Cmd/Ctrl+K → focus search (always, even when typing)
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      focusSearch()
      return
    }

    // Remaining shortcuts only fire when not typing
    if (isTyping(event)) return

    // Don't fire if any modifier key is held (other than shift)
    if (event.metaKey || event.ctrlKey || event.altKey) return

    switch (event.key) {
      case 'n':
      case 'N':
        event.preventDefault()
        router.push({ path: '/cm/pipeline', query: { action: 'add-deal' } })
        break
      case 'l':
      case 'L':
        event.preventDefault()
        router.push({ path: '/cm/today', query: { action: 'log-activity' } })
        break
      case 'p':
      case 'P':
        event.preventDefault()
        router.push('/cm/pipeline')
        break
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown)
  })
}
